import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  Clock, 
  Play, 
  Pause,
  Calendar,
  Target,
  TrendingUp,
  CheckCircle2
} from 'lucide-react';

export function CurrentLearningTab() {
  const currentCourses = [
    {
      id: 1,
      title: 'Philosophy of Mind',
      instructor: 'Dr. Sarah Johnson',
      progress: 85,
      timeSpent: '12h 30m',
      nextSession: 'Today, 2:00 PM',
      status: 'active',
      difficulty: 'Intermediate',
      estimatedCompletion: '2 weeks'
    },
    {
      id: 2,
      title: 'Logic and Reasoning',
      instructor: 'Prof. Michael Chen',
      progress: 60,
      timeSpent: '8h 15m',
      nextSession: 'Tomorrow, 10:00 AM',
      status: 'active',
      difficulty: 'Beginner',
      estimatedCompletion: '3 weeks'
    },
    {
      id: 3,
      title: 'Ethics in Practice',
      instructor: 'Dr. Emily Rodriguez',
      progress: 40,
      timeSpent: '5h 45m',
      nextSession: 'Wednesday, 3:00 PM',
      status: 'paused',
      difficulty: 'Advanced',
      estimatedCompletion: '4 weeks'
    }
  ];

  const weeklyGoals = [
    { label: 'Study Hours', current: 12, target: 15, unit: 'hours' },
    { label: 'Modules Completed', current: 3, target: 5, unit: 'modules' },
    { label: 'Assignments', current: 2, target: 3, unit: 'completed' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-black">Current Learning</h1>
          <p className="text-gray-600">Track your active courses and progress</p>
        </div>
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          3 Active Courses
        </Badge>
      </div>

      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="text-black flex items-center gap-2">
            <Target className="h-5 w-5" />
            This Week's Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {weeklyGoals.map((goal, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-black">{goal.label}</span>
                  <span className="text-sm text-gray-600">
                    {goal.current}/{goal.target} {goal.unit}
                  </span>
                </div>
                <Progress value={(goal.current / goal.target) * 100} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {currentCourses.map((course) => (
          <Card key={course.id} className="bg-white border border-gray-200">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-black">{course.title}</h3>
                      <p className="text-sm text-gray-600">by {course.instructor}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={course.status === 'active' ? 'default' : 'secondary'}
                        className={course.status === 'active' ? 'bg-green-100 text-green-700' : ''}
                      >
                        {course.status === 'active' ? 'Active' : 'Paused'}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {course.difficulty}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium text-black">{course.progress}% complete</span>
                    </div>
                    <Progress value={course.progress} className="h-2" />
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{course.timeSpent} spent</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{course.nextSession}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <TrendingUp className="h-4 w-4" />
                      <span>{course.estimatedCompletion} remaining</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-row lg:flex-col gap-2 lg:w-32">
                  <Button 
                    className="flex-1 lg:flex-none" 
                    size="sm"
                    disabled={course.status === 'paused'}
                  >
                    {course.status === 'active' ? (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Continue
                      </>
                    ) : (
                      <>
                        <Pause className="h-4 w-4 mr-2" />
                        Resume
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 lg:flex-none">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="text-black">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button variant="outline" className="justify-start">
              <Calendar className="h-4 w-4 mr-2" />
              View Schedule
            </Button>
            <Button variant="outline" className="justify-start">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Submit Assignment
            </Button>
            <Button variant="outline" className="justify-start">
              <BookOpen className="h-4 w-4 mr-2" />
              Access Library
            </Button>
            <Button variant="outline" className="justify-start">
              <Target className="h-4 w-4 mr-2" />
              Set Goals
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
